import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { produce } from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { useBlocker } from 'react-router';
import { v4 } from 'uuid';
import { ExtractKeysExtend } from '../../../../../../../components/helpers/helpers';
import { AddOutline, SaveOutline, TrashOutline, UndoOutline, UserGroupOutline, UserOutline } from '../../../../../../../icons/icons';
import { EffectiveMonitorAircraftOptions, mergeAircraftOptions } from '../../../../../../../monitoring/merger/monitor-options-merger';
import { defaultAircraftMonitorOptions } from '../../../../../../../monitoring/monitor-defaults';
import { CompanyThresholdsMode, MonitorAircraftOptions, MonitorAircraftSettingsBeaconCode } from '../../../../../../../types/thresholds';
import { LoadingIndicatorCircular } from '../../../../../../components/indicators/LoadingIndicatorCircular';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store-hooks';
import { updateMonitorSettings } from '../../../../../../state/slices/global-slice';
import { store } from '../../../../../../state/store';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../../context/SettingsNavContext';
import { ThresholdHeaderBar } from '../common/ThresholdHeaderBar';

type Tab = 'user' | 'company';
// Need IDs for react
type BeaconCodeItem = MonitorAircraftSettingsBeaconCode & { id: string };
type DataItem = Omit<MonitorAircraftOptions, 'emergencyBeacons'> & {
    emergencyBeacons: BeaconCodeItem[];
};
type Data = {
    user: DataItem;
    company: DataItem;
};

function useEditContext() {
    const [edited, setEdited] = useState(false);
    const [tab, setTab] = useState<Tab>('user');
    const [mode, setMode] = useState(() => {
        return store.getState().global.monitorSettings.company.mode;
    });

    const updateMode = useCallback((mode: CompanyThresholdsMode) => {
        setMode(mode);
        setEdited(true);
    }, []);

    const [data, setData] = useState<Data>(() => {
        const state = store.getState();
        return {
            user: {
                ...state.global.monitorSettings.user.aircraft.options,
                emergencyBeacons: state.global.monitorSettings.user.aircraft.options.emergencyBeacons.map((x) => ({
                    ...x,
                    id: v4()
                }))
            },
            company: {
                ...state.global.monitorSettings.company.aircraft.options,
                emergencyBeacons: state.global.monitorSettings.company.aircraft.options.emergencyBeacons.map((x) => ({
                    ...x,
                    id: v4()
                }))
            }
        } satisfies Data;
    });

    const displayData = useMemo(() => mergeAircraftOptions(tab, data, mode), [tab, data, mode]);

    const updateRow = useCallback(
        (recipe: (draft: DataItem) => void) => {
            setEdited(true);
            setData((p) => ({
                ...p,
                [tab]: produce(p[tab], (d) => {
                    recipe(d);
                })
            }));
        },
        [tab]
    );

    const getCompanyValue = useCallback(
        <T,>(getter: (options: MonitorAircraftOptions) => T) => {
            if (tab === 'user' && mode === CompanyThresholdsMode.minimum) {
                return getter(data.company) ?? getter(defaultAircraftMonitorOptions);
            }
            return null;
        },
        [tab, data, mode]
    );

    const clearEdited = useCallback(() => {
        setEdited(false);
    }, []);

    const restoreDefaults = useCallback(() => {
        setEdited(true);
        setData((p) => ({
            ...p,
            [tab]: {
                // Set everything to null which will resolve to defaults anyway
                monitorOriginDepartureMinMinutes: null,
                monitorOriginWithoutDepartureTime: null,
                monitorDestinationDepartureMinMinutes: null,
                monitorDestinationWithoutArrivalTime: null,
                monitorAltDepartureMinMinutes: null,
                monitorAltWithoutArrivalTime: null,
                ignoredCompanyBeaconCodes: [],
                altitudeChangeWindowMinutes: null,
                suppressAltitudeMonitorMiles: null,
                monitorImpactDistance: {
                    volcanicAsh: null,
                    sigmet: null,
                    convSigment: null,
                    dsua: null,
                    edrTurbulence: null,
                    turbulence: null,
                    icing: null,
                    contrail: null,
                    tcf: null
                },
                emergencyBeacons: defaultAircraftMonitorOptions.emergencyBeacons.map((x) => ({ ...x, id: v4() }))
            } satisfies DataItem
        }));
    }, [tab]);

    return {
        tab,
        mode,
        data,
        edited,
        displayData,
        clearEdited,
        updateRow,
        setTab,
        updateMode,
        getCompanyValue,
        restoreDefaults
    };
}

function NumericBox(props: {
    context: ReturnType<typeof useEditContext>;
    name: ExtractKeysExtend<EffectiveMonitorAircraftOptions, number>;
    disabled?: boolean;
    width?: string | number;
    applyCompanyAsBound?: 'upper' | 'lower';
    min: number;
    max: number;
}) {
    const boundType = props.applyCompanyAsBound ?? 'lower';
    const companyValue = props.context.getCompanyValue((g) => g[props.name]);
    return (
        <NumericTextBox
            width={props.width}
            disabled={props.disabled}
            min={(boundType === 'lower' ? companyValue : null) ?? props.min}
            max={(boundType === 'upper' ? companyValue : null) ?? props.max}
            value={props.context.displayData![props.name]}
            onChange={(e) => props.context.updateRow((p) => (p[props.name] = e.value))}
        />
    );
}

function NumericBoxImpacts(props: {
    context: ReturnType<typeof useEditContext>;
    title: string;
    disabled?: boolean;
    name: ExtractKeysExtend<MonitorAircraftOptions['monitorImpactDistance'], number | null>;
    width?: string | number;
    min?: number;
    max?: number;
}) {
    const companyMin = props.context.getCompanyValue((g) => g.monitorImpactDistance[props.name]);

    return (
        <>
            <div>
                <div>{props.title}</div>
                {companyMin !== null && (
                    <div className="secondary-text d-flex gap-1 align-items-center">Company minimum: {companyMin} NM</div>
                )}
            </div>
            <NumericTextBox
                disabled={props.disabled}
                width={props.width}
                min={companyMin ?? props.min}
                max={props.max}
                value={props.context.displayData!.monitorImpactDistance![props.name]}
                onChange={(e) => props.context.updateRow((p) => (p.monitorImpactDistance[props.name] = e.value))}
            />
        </>
    );
}

export default function SettingsAircraftThresholdsOptions() {
    useSettingsPageWidth('700px');
    const [busy, setBusy] = useState(false);
    const canEditCompanyThresholds = useAppSelector((x) => x.user.rights.canEditCompanyThresholds);

    const context = useEditContext();
    const dispatch = useAppDispatch();
    const blocker = useBlocker(context.edited);

    const isUserAndMinimum = context.tab === 'user' && context.mode === CompanyThresholdsMode.minimum;
    const isUserAndMinimumOrTemplate =
        context.tab === 'user' && (context.mode === CompanyThresholdsMode.minimum || context.mode === CompanyThresholdsMode.template);
    const readonly = context.tab === 'company' && !canEditCompanyThresholds;
    const rawData = context.data?.[context.tab];

    const handleSave = async () => {
        try {
            setBusy(true);
            const result = await dispatch(
                updateMonitorSettings({
                    user: {
                        aircraft: { options: context.data?.user }
                    },
                    company: canEditCompanyThresholds
                        ? { aircraft: { options: context.data?.company }, companyMode: context.mode }
                        : undefined
                })
            ).unwrap();
            if (result) {
                context.clearEdited();
            }
            return result;
        } catch {
            // Already handled
        } finally {
            setBusy(false);
        }

        return false;
    };

    const handleSaveConfirm = async () => {
        const result = await handleSave();
        if (result) {
            if (blocker.state === 'blocked') {
                blocker.proceed();
            }
        }
    };

    return (
        <div className="pb-2 h-100">
            {blocker.state === 'blocked' && (
                <Dialog title="Airport thresholds" onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar>
                        <Button themeColor={'primary'} onClick={handleSaveConfirm} disabled={busy}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()} disabled={busy}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}

            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Aircraft threshold options</div>
                    {busy && <LoadingIndicatorCircular />}
                    <Button svgIcon={SaveOutline} fillMode={'outline'} disabled={!context.edited} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </SettingsNavTitle>

            <div className="k-card max-h-100">
                <ThresholdHeaderBar
                    hideClearAll
                    tab={context.tab}
                    companyMode={context.mode}
                    onChangeTab={context.setTab}
                    onChangeCompanyMode={context.updateMode}
                    onRestoreDefaultClicked={context.restoreDefaults}
                />

                {context.displayData && (
                    <div className="overflow-y-auto">
                        <div className="border-bottom p-2 px-2">
                            <div className="secondary-text">Flight monitor time window</div>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'max-content 1fr',
                                    alignItems: 'start',
                                    columnGap: '0.5rem',
                                    rowGap: '0.5rem'
                                }}>
                                <div>Origin:</div>

                                <div>
                                    <div>
                                        <span>Monitor origin airport from </span>
                                        <span>
                                            <NumericBox
                                                disabled={readonly}
                                                context={context}
                                                name="monitorOriginDepartureMinMinutes"
                                                width={125}
                                                max={240}
                                                min={0}
                                            />
                                        </span>
                                        <span> minutes before planned departure time until actual departure.</span>
                                    </div>
                                    <Checkbox
                                        label="Monitor origin for flights without departure time"
                                        value={context.displayData.monitorOriginWithoutDepartureTime}
                                        onChange={(e) => context.updateRow((x) => (x.monitorOriginWithoutDepartureTime = e.value))}
                                        disabled={
                                            readonly ||
                                            (isUserAndMinimum &&
                                                !!context.getCompanyValue((x) => x.monitorOriginWithoutDepartureTime) === true)
                                        }
                                    />
                                </div>

                                <div>Destination:</div>
                                <div>
                                    <div>
                                        <span>Monitor destination airport from </span>
                                        <span>
                                            <NumericBox
                                                disabled={readonly}
                                                context={context}
                                                name="monitorDestinationDepartureMinMinutes"
                                                width={125}
                                                max={360}
                                                min={0}
                                            />
                                        </span>
                                        <span> minutes before estimated time of arrival until flight is complete.</span>
                                    </div>
                                    <Checkbox
                                        label="Monitor destination for flights without arrival time"
                                        value={context.displayData.monitorDestinationWithoutArrivalTime}
                                        onChange={(e) => context.updateRow((x) => (x.monitorDestinationWithoutArrivalTime = e.value))}
                                        disabled={
                                            readonly ||
                                            (isUserAndMinimum &&
                                                !!context.getCompanyValue((x) => x.monitorDestinationWithoutArrivalTime) === true)
                                        }
                                    />
                                </div>

                                <div>Destination alternate:</div>
                                <div>
                                    <div>
                                        <span>Monitor destination alternates from </span>
                                        <span>
                                            <NumericBox
                                                disabled={readonly}
                                                context={context}
                                                name="monitorAltDepartureMinMinutes"
                                                width={125}
                                                max={360}
                                                min={0}
                                            />
                                        </span>
                                        <span> minutes before estimated arrival until actual arrival.</span>
                                    </div>
                                    <Checkbox
                                        label="Monitor destination alternates for flights without arrival time"
                                        value={context.displayData.monitorAltWithoutArrivalTime}
                                        onChange={(e) => context.updateRow((x) => (x.monitorAltWithoutArrivalTime = e.value))}
                                        disabled={
                                            readonly ||
                                            (isUserAndMinimum && context.getCompanyValue((x) => x.monitorAltWithoutArrivalTime) === true)
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-bottom p-2">
                            <div className="secondary-text">Emergency beacon codes</div>
                            <BeaconCodes
                                onAddCode={() =>
                                    context.updateRow((p) =>
                                        p.emergencyBeacons.push({
                                            code: 0,
                                            text: '',
                                            id: v4()
                                        })
                                    )
                                }
                                onRemoveCode={(id) =>
                                    context.updateRow((p) => {
                                        p.emergencyBeacons = p.emergencyBeacons.filter((x) => x.id !== id);
                                    })
                                }
                                onPatchRow={(id, update) =>
                                    context.updateRow((p) => {
                                        const item = p.emergencyBeacons.find((x) => x.id === id);
                                        if (item) {
                                            Object.assign(item, update);
                                        }
                                    })
                                }
                                onIgnoreRow={(code, ignore) =>
                                    context.updateRow((x) => {
                                        if (ignore) {
                                            x.ignoredCompanyBeaconCodes.push(code);
                                        } else {
                                            x.ignoredCompanyBeaconCodes = x.ignoredCompanyBeaconCodes.filter((x) => x !== code);
                                        }
                                    })
                                }
                                readonly={context.tab === 'company' && !canEditCompanyThresholds}
                                codes={rawData!.emergencyBeacons}
                                ignoredCodes={rawData!.ignoredCompanyBeaconCodes}
                                inheritedCodes={isUserAndMinimumOrTemplate ? context.data!.company.emergencyBeacons : []}
                            />
                        </div>

                        <div className="border-bottom p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>Altitude change window (minutes)</div>
                                <NumericBox
                                    context={context}
                                    name="altitudeChangeWindowMinutes"
                                    width={125}
                                    min={0}
                                    max={999}
                                    disabled={readonly}
                                />
                            </div>
                        </div>

                        <div className="border-bottom p-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>Suppress altitude monitor within x miles of origin/dest</div>
                                <NumericBox
                                    context={context}
                                    disabled={readonly}
                                    name="suppressAltitudeMonitorMiles"
                                    width={125}
                                    min={0}
                                    max={999}
                                    applyCompanyAsBound="upper"
                                />
                            </div>
                        </div>

                        {/* Impacts */}
                        <div className="border-bottom p-2">
                            <div className="secondary-text">Route impact detection distance (0=direct intersection only)</div>
                            <div
                                className="pt-2"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 125px 1fr 125px',
                                    alignItems: 'center',
                                    columnGap: '1rem',
                                    rowGap: '0.5rem'
                                }}>
                                <NumericBoxImpacts
                                    title="Volcanic ash (0-200 NM)"
                                    context={context}
                                    name="volcanicAsh"
                                    min={0}
                                    max={200}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    disabled={readonly}
                                    title="EDR Turbulence (0-25 NM)"
                                    context={context}
                                    name="edrTurbulence"
                                    min={0}
                                    max={25}
                                />
                                <NumericBoxImpacts
                                    title="SIGMET (0-50 NM)"
                                    context={context}
                                    name="sigmet"
                                    min={0}
                                    max={50}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    title="Turbulence (0-25 NM)"
                                    context={context}
                                    name="turbulence"
                                    min={0}
                                    max={25}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    title="Convective SIGMET (0-50 NM)"
                                    disabled={readonly}
                                    context={context}
                                    name="convSigment"
                                    min={0}
                                    max={50}
                                />
                                <NumericBoxImpacts
                                    title="Icing (0-25 NM)"
                                    context={context}
                                    name="icing"
                                    min={0}
                                    max={25}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    title="DSUA (0-50 NM)"
                                    context={context}
                                    name="dsua"
                                    min={0}
                                    max={50}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    title="Contrail (0-25 NM)"
                                    context={context}
                                    name="contrail"
                                    min={0}
                                    max={25}
                                    disabled={readonly}
                                />
                                <NumericBoxImpacts
                                    title="TCF (0-25 NM)"
                                    context={context}
                                    name="tcf"
                                    min={0}
                                    max={25}
                                    disabled={readonly}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function BeaconCodes(props: {
    codes: BeaconCodeItem[];
    inheritedCodes: BeaconCodeItem[];
    ignoredCodes: number[];
    readonly: boolean;
    onAddCode: () => void;
    onPatchRow: (id: string, update: Partial<BeaconCodeItem>) => void;
    onRemoveCode: (id: string) => void;
    onIgnoreRow: (code: number, ignore: boolean) => void;
}) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: '100px 1fr max-content',
                columnGap: '0.5rem',
                rowGap: '0.5rem',
                alignItems: 'end'
            }}>
            <div>Code</div>
            <div>Description</div>
            <div>
                <Button
                    onClick={props.onAddCode}
                    svgIcon={AddOutline}
                    fillMode={'outline'}
                    themeColor={'primary'}
                    disabled={props.readonly}
                />
            </div>
            {props.inheritedCodes.length > 0 && (
                <>
                    <div style={{ gridColumn: '1/4' }} className="d-flex align-items-center gap-1">
                        <SvgIcon icon={UserGroupOutline} />
                        Inherited company codes (readonly)
                    </div>
                    {props.inheritedCodes.map((item) => (
                        <div style={{ display: 'contents' }} key={item.id}>
                            <NumericTextBox format="0000" value={item.code} readOnly spinners={false} />
                            <TextBox value={item.text} readOnly />
                            <Button
                                svgIcon={props.ignoredCodes.includes(item.code) ? UndoOutline : TrashOutline}
                                fillMode={'outline'}
                                title={props.ignoredCodes.includes(item.code) ? 'Restore' : 'Ignore'}
                                themeColor={props.ignoredCodes.includes(item.code) ? 'success' : 'error'}
                                onClick={() => props.onIgnoreRow(item.code, !props.ignoredCodes.includes(item.code))}
                            />
                        </div>
                    ))}
                    <div style={{ gridColumn: '1/4' }} className="d-flex align-items-center gap-1">
                        <SvgIcon icon={UserOutline} />
                        User codes
                    </div>
                </>
            )}

            {props.codes.map((item) => (
                <div style={{ display: 'contents' }} key={item.id}>
                    <NumericTextBox
                        spinners={false}
                        min={0}
                        format={'0000'}
                        value={item.code}
                        max={7777}
                        valid={props.codes.every((x) => x.id === item.id || x.code !== item.code)}
                        onKeyDown={(e) => {
                            if (e.key >= '8' && e.key <= '9') {
                                e.preventDefault();
                            }
                        }}
                        onChange={(e) => {
                            props.onPatchRow(item.id, { code: e.value as number });
                        }}
                    />
                    <TextBox
                        placeholder="Description"
                        value={item.text}
                        onChange={(e) => props.onPatchRow(item.id, { text: e.value as string })}
                    />
                    <Button svgIcon={TrashOutline} fillMode={'outline'} themeColor={'error'} onClick={() => props.onRemoveCode(item.id)} />
                </div>
            ))}
            {props.codes.length === 0 && (
                <div className="secondary-text" style={{ gridColumn: '1/4' }}>
                    No emergency beacon codes
                </div>
            )}
        </div>
    );
}
